import type { RoutePoint } from '@/types/running';

/**
 * Returns a color based on AQI value using the standard AQI color scale.
 * Green (good) → Yellow (moderate) → Orange (sensitive) → Red (unhealthy) → Purple → Maroon
 */
export function getAqiColor(aqi: number | undefined): string {
  if (aqi === undefined || aqi === null) return '#10b981'; // default emerald
  if (aqi <= 50) return '#22c55e';    // green — Good
  if (aqi <= 100) return '#eab308';   // yellow — Moderate
  if (aqi <= 150) return '#f97316';   // orange — Unhealthy for Sensitive
  if (aqi <= 200) return '#ef4444';   // red — Unhealthy
  if (aqi <= 300) return '#a855f7';   // purple — Very Unhealthy
  return '#991b1b';                    // maroon — Hazardous
}

/**
 * Returns a glow color (lighter version) for an AQI-based segment.
 */
export function getAqiGlowColor(aqi: number | undefined): string {
  if (aqi === undefined || aqi === null) return '#06b6d4';
  if (aqi <= 50) return '#86efac';
  if (aqi <= 100) return '#fde047';
  if (aqi <= 150) return '#fdba74';
  if (aqi <= 200) return '#fca5a5';
  if (aqi <= 300) return '#d8b4fe';
  return '#dc2626';
}

/** Check if any route points have AQI data */
function hasAqiData(points: RoutePoint[]): boolean {
  return points.some(p => p.aqi !== undefined && p.aqi !== null);
}

/**
 * Renders GPS route points as a shareable image.
 * Route segments are colored by AQI when RoutePoints have aqi values.
 * Supports both solid and transparent backgrounds (for photo overlay).
 */
export function renderRouteArt(
  routePoints: RoutePoint[],
  width = 600,
  height = 400,
  options?: {
    strokeColor?: string;
    glowColor?: string;
    backgroundColor?: string | null; // null = transparent
    padding?: number;
    lineWidth?: number;
    glowWidth?: number;
  }
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  const {
    strokeColor = '#10b981',
    glowColor = '#06b6d4',
    backgroundColor = '#0a0f14',
    padding = 40,
    lineWidth = 3,
    glowWidth = 8,
  } = options || {};

  // Fill background (skip if transparent)
  if (backgroundColor) {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
  } else {
    ctx.clearRect(0, 0, width, height);
  }

  if (routePoints.length < 2) {
    ctx.fillStyle = '#ffffff20';
    ctx.font = '24px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('Indoor Activity', width / 2, height / 2);
    return canvas;
  }

  // Bounding box
  const lats = routePoints.map(p => p.lat);
  const lngs = routePoints.map(p => p.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);

  const drawWidth = width - padding * 2;
  const drawHeight = height - padding * 2;
  const latRange = maxLat - minLat || 0.001;
  const lngRange = maxLng - minLng || 0.001;
  const scale = Math.min(drawWidth / lngRange, drawHeight / latRange);

  // Center the route
  const routeW = lngRange * scale;
  const routeH = latRange * scale;
  const offsetX = padding + (drawWidth - routeW) / 2;
  const offsetY = padding + (drawHeight - routeH) / 2;

  const toX = (lng: number) => offsetX + (lng - minLng) * scale;
  const toY = (lat: number) => height - offsetY - (lat - minLat) * scale;

  const useAqiColors = hasAqiData(routePoints);

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (useAqiColors) {
    // Draw AQI-colored segments: glow pass then main pass
    // Glow pass
    ctx.lineWidth = glowWidth;
    ctx.globalAlpha = 0.3;
    for (let i = 1; i < routePoints.length; i++) {
      const segAqi = routePoints[i - 1].aqi ?? routePoints[i].aqi;
      ctx.strokeStyle = getAqiGlowColor(segAqi);
      ctx.beginPath();
      ctx.moveTo(toX(routePoints[i - 1].lng), toY(routePoints[i - 1].lat));
      ctx.lineTo(toX(routePoints[i].lng), toY(routePoints[i].lat));
      ctx.stroke();
    }

    // Main line pass
    ctx.lineWidth = lineWidth;
    ctx.globalAlpha = 1;
    for (let i = 1; i < routePoints.length; i++) {
      const segAqi = routePoints[i - 1].aqi ?? routePoints[i].aqi;
      ctx.strokeStyle = getAqiColor(segAqi);
      ctx.beginPath();
      ctx.moveTo(toX(routePoints[i - 1].lng), toY(routePoints[i - 1].lat));
      ctx.lineTo(toX(routePoints[i].lng), toY(routePoints[i].lat));
      ctx.stroke();
    }
  } else {
    // Fallback: single-color line (no AQI data)
    // Glow
    ctx.strokeStyle = glowColor;
    ctx.lineWidth = glowWidth;
    ctx.globalAlpha = 0.3;
    ctx.beginPath();
    ctx.moveTo(toX(routePoints[0].lng), toY(routePoints[0].lat));
    for (let i = 1; i < routePoints.length; i++) {
      ctx.lineTo(toX(routePoints[i].lng), toY(routePoints[i].lat));
    }
    ctx.stroke();

    // Main
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = lineWidth;
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.moveTo(toX(routePoints[0].lng), toY(routePoints[0].lat));
    for (let i = 1; i < routePoints.length; i++) {
      ctx.lineTo(toX(routePoints[i].lng), toY(routePoints[i].lat));
    }
    ctx.stroke();
  }

  // Start marker (green)
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#10b981';
  ctx.beginPath();
  ctx.arc(toX(routePoints[0].lng), toY(routePoints[0].lat), 6, 0, Math.PI * 2);
  ctx.fill();

  // End marker (red)
  ctx.fillStyle = '#ef4444';
  ctx.beginPath();
  ctx.arc(
    toX(routePoints[routePoints.length - 1].lng),
    toY(routePoints[routePoints.length - 1].lat),
    6, 0, Math.PI * 2
  );
  ctx.fill();

  return canvas;
}
